/* Community Newsletter Gallery Carousel
 * Handles the carousel navigation for the gallery images
 */

class CommunityNewsletterCarousel {
  constructor(galleryElement) {
    if (galleryElement.dataset.carouselInitialized) return;
    galleryElement.dataset.carouselInitialized = "true";

    this.gallery = galleryElement;
    this.galleryId = galleryElement.id;
    this.wrapper = galleryElement.closest(
      ".community-newsletter__gallery-wrapper",
    );
    this.prevBtn = document.querySelector(
      `.community-newsletter__nav-btn--prev[data-gallery-id="${this.galleryId}"]`,
    );
    this.nextBtn = document.querySelector(
      `.community-newsletter__nav-btn--next[data-gallery-id="${this.galleryId}"]`,
    );
    this.scrollAmount = 0;
    this.beholdFeedUrl = galleryElement.dataset.beholdFeedUrl;

    this.init();
  }

  async init() {
    if (!this.gallery) {
      console.warn("CommunityNewsletterCarousel: Gallery element not found");
      return;
    }

    if (!this.prevBtn || !this.nextBtn) {
      console.warn(
        "CommunityNewsletterCarousel: Navigation buttons not found",
        {
          prevBtn: !!this.prevBtn,
          nextBtn: !!this.nextBtn,
          galleryId: this.galleryId,
        },
      );
      return;
    }

    // Load Instagram feed if Behold URL is provided
    if (this.beholdFeedUrl) {
      await this.loadInstagramFeed();
    }

    // Prevent default button behavior
    this.prevBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.scrollPrev();
    });

    this.nextBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.scrollNext();
    });

    // Update button states on scroll
    this.gallery.addEventListener("scroll", () => this.updateButtonStates());

    // Initial button state (with delay to ensure DOM is ready)
    setTimeout(() => {
      this.updateButtonStates();
    }, 100);

    // Update on resize
    window.addEventListener("resize", () => {
      setTimeout(() => this.updateButtonStates(), 100);
    });
  }

  async loadInstagramFeed() {
    try {
      const response = await fetch(this.beholdFeedUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch Instagram feed: ${response.status}`);
      }

      const data = await response.json();

      // Clear placeholder
      this.gallery.innerHTML = "";

      // Get posts from the data.posts array
      const posts = (data.posts || []).slice(0, 20);

      if (posts.length === 0) {
        console.warn("No Instagram posts found in Behold feed");
        this.gallery.innerHTML =
          '<div class="community-newsletter__gallery-item community-newsletter__placeholder">No posts available</div>';
        return;
      }

      // Create gallery items from Instagram posts
      posts.forEach((post) => {
        const item = document.createElement("div");
        item.className = "community-newsletter__gallery-item";

        const link = document.createElement("a");
        link.href = post.permalink || "#";
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.style.display = "block";
        link.style.width = "100%";
        link.style.height = "100%";
        link.style.position = "relative";

        const isVideo = post.mediaType === "VIDEO";

        if (isVideo) {
          // Render Reels / Videos as thumbnail image with play icon overlay
          const thumbSrc =
            post.thumbnailUrl ||
            (post.sizes && post.sizes.medium
              ? post.sizes.medium.mediaUrl
              : "") ||
            post.mediaUrl ||
            "";
          const img = document.createElement("img");
          img.src = thumbSrc;
          img.alt = post.prunedCaption || post.caption || "Instagram reel";
          img.loading = "lazy";
          img.style.width = "100%";
          img.style.height = "100%";
          img.style.objectFit = "cover";
          img.style.display = "block";

          link.appendChild(img);

          // Add a play icon overlay so users know it's a reel
          const playIcon = document.createElement("div");
          playIcon.className = "community-newsletter__reel-icon";
          playIcon.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M9.5 7.5v9l7-4.5-7-4.5z"/></svg>`;
          link.appendChild(playIcon);
        } else {
          // Render regular IMAGE posts
          const imgSrc =
            (post.sizes && post.sizes.medium
              ? post.sizes.medium.mediaUrl
              : "") ||
            post.mediaUrl ||
            "";
          const img = document.createElement("img");
          img.src = imgSrc;
          img.alt = post.prunedCaption || post.caption || "Instagram post";
          img.loading = "lazy";
          img.style.width = "100%";
          img.style.height = "100%";
          img.style.objectFit = "cover";
          img.style.display = "block";

          link.appendChild(img);
        }

        item.appendChild(link);
        this.gallery.appendChild(item);
      });

      // Update button states after images are loaded
      setTimeout(() => this.updateButtonStates(), 200);
    } catch (error) {
      console.error("Error loading Instagram feed:", error);
      this.gallery.innerHTML =
        '<div class="community-newsletter__gallery-item community-newsletter__placeholder">Failed to load Instagram feed</div>';
    }
  }

  scrollPrev() {
    if (!this.gallery) return;

    // Check if button is disabled
    if (
      this.prevBtn.classList.contains("disabled") ||
      this.prevBtn.getAttribute("aria-disabled") === "true"
    ) {
      return; // Don't scroll if disabled
    }

    const scrollLeft = this.gallery.scrollLeft;
    if (scrollLeft <= 5) return; // Already at start

    const itemWidth =
      this.gallery.querySelector(".community-newsletter__gallery-item")
        ?.offsetWidth || 272.839;
    const styles = getComputedStyle(this.gallery);
    const gap = parseInt(styles.columnGap || styles.gap || "16") || 16;
    const scrollDistance = itemWidth + gap;
    const newScroll = Math.max(0, scrollLeft - scrollDistance);

    this.gallery.scrollTo({
      left: newScroll,
      behavior: "smooth",
    });
  }

  scrollNext() {
    if (!this.gallery) return;

    // Check if button is disabled
    if (
      this.nextBtn.classList.contains("disabled") ||
      this.nextBtn.getAttribute("aria-disabled") === "true"
    ) {
      return; // Don't scroll if disabled
    }

    const scrollLeft = this.gallery.scrollLeft;
    const maxScroll = this.gallery.scrollWidth - this.gallery.clientWidth;
    if (scrollLeft >= maxScroll - 5) return; // Already at end

    const itemWidth =
      this.gallery.querySelector(".community-newsletter__gallery-item")
        ?.offsetWidth || 272.839;
    const styles = getComputedStyle(this.gallery);
    const gap = parseInt(styles.columnGap || styles.gap || "16") || 16;
    const scrollDistance = itemWidth + gap;
    const newScroll = Math.min(maxScroll, scrollLeft + scrollDistance);

    this.gallery.scrollTo({
      left: newScroll,
      behavior: "smooth",
    });
  }

  updateButtonStates() {
    const scrollLeft = this.gallery.scrollLeft;
    const maxScroll = this.gallery.scrollWidth - this.gallery.clientWidth;

    // Show/hide fade effect based on scroll position
    if (this.wrapper) {
      if (scrollLeft > 5) {
        this.wrapper.classList.add("is-scrolled");
      } else {
        this.wrapper.classList.remove("is-scrolled");
      }
    }

    // Update prev button state - gray when at start
    if (scrollLeft <= 5) {
      this.prevBtn.setAttribute("aria-disabled", "true");
      this.prevBtn.classList.add("disabled");
      this.prevBtn.style.setProperty("background", "#262626", "important");
      this.prevBtn.style.setProperty(
        "background-color",
        "#262626",
        "important",
      );
      this.prevBtn.style.setProperty("color", "#fafafa", "important");
      this.prevBtn.style.cursor = "not-allowed";
      this.prevBtn.style.opacity = "1";
    } else {
      this.prevBtn.removeAttribute("aria-disabled");
      this.prevBtn.classList.remove("disabled");
      this.prevBtn.style.setProperty("background", "#fafafa", "important");
      this.prevBtn.style.setProperty(
        "background-color",
        "#fafafa",
        "important",
      );
      this.prevBtn.style.setProperty("color", "#0a0a0a", "important");
      this.prevBtn.style.cursor = "pointer";
      this.prevBtn.style.opacity = "1";
    }

    // Update next button state - gray when at end
    if (maxScroll <= 5 || scrollLeft >= maxScroll - 5) {
      this.nextBtn.setAttribute("aria-disabled", "true");
      this.nextBtn.classList.add("disabled");
      this.nextBtn.style.setProperty("background", "#262626", "important");
      this.nextBtn.style.setProperty(
        "background-color",
        "#262626",
        "important",
      );
      this.nextBtn.style.setProperty("color", "#fafafa", "important");
      this.nextBtn.style.cursor = "not-allowed";
      this.nextBtn.style.opacity = "1";
    } else {
      this.nextBtn.removeAttribute("aria-disabled");
      this.nextBtn.classList.remove("disabled");
      this.nextBtn.style.setProperty("background", "#fafafa", "important");
      this.nextBtn.style.setProperty(
        "background-color",
        "#fafafa",
        "important",
      );
      this.nextBtn.style.setProperty("color", "#0a0a0a", "important");
      this.nextBtn.style.cursor = "pointer";
      this.nextBtn.style.opacity = "1";
    }
  }
}

// Initialize all carousels when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCommunityCarousels);
} else {
  initCommunityCarousels();
}

function initCommunityCarousels() {
  const galleries = document.querySelectorAll('[id^="community-gallery-"]');
  galleries.forEach((gallery) => {
    new CommunityNewsletterCarousel(gallery);
  });
}

// Reinitialize on Shopify theme editor section load
if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", () => {
    setTimeout(initCommunityCarousels, 100);
  });
}

// Also reinitialize after images load
window.addEventListener("load", () => {
  setTimeout(initCommunityCarousels, 200);
});
